import React, { useState, useEffect } from 'react';
import { BrickCharts, SearchEngine } from 'brickcharts/dist/brickcharts.es.js';

function ChartSearch() {
  const [searchEngine, setSearchEngine] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('artist');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchStats, setSearchStats] = useState(null);

  useEffect(() => {
    const initializeSearch = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch chart data for search indexing
        const brickCharts = new BrickCharts({
          enableCache: true
        });

        // Fetch multiple chart types for comprehensive search
        const chartTypes = ['hot-100', 'billboard-200', 'artist-100'];
        const allChartData = [];

        for (const chartType of chartTypes) {
          try {
            const chart = await brickCharts.getChart(chartType);
            allChartData.push(chart);
          } catch (error) {
            console.warn(`Failed to fetch ${chartType}:`, error.message);
          }
        }

        setChartData(allChartData);

        // Initialize search engine
        const engine = new SearchEngine();
        engine.indexChartData(allChartData);
        setSearchEngine(engine);

        // Set initial search stats
        const totalEntries = allChartData.reduce((total, chart) => 
          total + (chart.entries?.length || 0), 0
        );
        setSearchStats({
          totalCharts: allChartData.length,
          totalEntries,
          indexedSources: chartTypes
        });

      } catch (error) {
        setError(error.message);
        console.error('Search initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeSearch();
  }, []);

  const handleSearch = (term, type = searchType) => {
    if (!searchEngine || !term.trim()) {
      setSearchResults([]);
      return;
    }

    const searchQuery = {};
    searchQuery[type] = term;

    try {
      const { results } = searchEngine.search({
        ...searchQuery,
        fuzzy: true,
        limit: 50
      });

      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    }
  };

  const handleQuickSearch = (term) => {
    if (!searchEngine || !term.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const suggestions = searchEngine.quickSearch(term, searchType);
      setSearchResults(suggestions.map(suggestion => ({
        entry: suggestion,
        score: 1
      })));
    } catch (error) {
      console.error('Quick search error:', error);
      setSearchResults([]);
    }
  };

  const handleAdvancedSearch = () => {
    if (!searchEngine || !searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const searchQuery = {
        [searchType]: searchTerm,
        fuzzy: true,
        limit: 50,
        includeMetadata: true
      };

      const { results } = searchEngine.search(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Advanced search error:', error);
      setSearchResults([]);
    }
  };

  const getSearchTypeIcon = (type) => {
    switch (type) {
      case 'artist': return '👤';
      case 'title': return '🎵';
      case 'album': return '💿';
      default: return '🔍';
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Initializing search engine...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h3>Search Initialization Error</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="chart-search">
      <div className="search-stats">
        {searchStats && (
          <div className="stats-grid">
            <div className="stat">
              <span className="stat-label">Indexed Charts</span>
              <span className="stat-value">{searchStats.totalCharts}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Entries</span>
              <span className="stat-value">{searchStats.totalEntries}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Sources</span>
              <span className="stat-value">{searchStats.indexedSources.join(', ')}</span>
            </div>
          </div>
        )}
      </div>

      <div className="search-controls">
        <div className="search-input-group">
          <input
            type="text"
            placeholder={`Search by ${searchType}...`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleSearch(e.target.value);
            }}
            className="search-input"
          />
          
          <select 
            value={searchType} 
            onChange={(e) => setSearchType(e.target.value)}
            className="search-type"
          >
            <option value="artist">👤 Artist</option>
            <option value="title">🎵 Title</option>
            <option value="album">💿 Album</option>
          </select>
        </div>

        <div className="search-buttons">
          <button 
            onClick={handleAdvancedSearch}
            className="advanced-search-btn"
          >
            🔍 Advanced Search
          </button>
          <button 
            onClick={() => handleQuickSearch(searchTerm)}
            className="quick-search-btn"
          >
            ⚡ Quick Search
          </button>
          <button 
            onClick={() => {
              setSearchTerm('');
              setSearchResults([]);
            }}
            className="clear-search-btn"
          >
            🗑️ Clear
          </button>
        </div>
      </div>

      <div className="search-results">
        <div className="results-header">
          <h3>Search Results ({searchResults.length})</h3>
          {searchResults.length > 0 && (
            <div className="results-info">
              <span>Showing {Math.min(searchResults.length, 50)} results</span>
            </div>
          )}
        </div>

        {searchResults.length > 0 ? (
          <div className="results-list">
            {searchResults.slice(0, 50).map((result, index) => (
              <div key={index} className="search-result">
                <div className="result-rank">
                  <span className="rank">#{result.entry.rank}</span>
                  <span className="chart-source">{result.entry.chartType || 'Unknown'}</span>
                </div>
                <div className="result-info">
                  <span className="title">{result.entry.title}</span>
                  <span className="artist">{result.entry.artist}</span>
                </div>
                <div className="result-details">
                  {result.score && (
                    <span className="score">Score: {result.score.toFixed(2)}</span>
                  )}
                  {result.entry.lastWeek && (
                    <span className={`change ${getChangeClass(result.entry.rank, result.entry.lastWeek)}`}>
                      {getChangeSymbol(result.entry.rank, result.entry.lastWeek)}
                    </span>
                  )}
                </div>
                {result.entry.metadata?.playcount && (
                  <span className="playcount">
                    {formatPlaycount(result.entry.metadata.playcount)} plays
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : searchTerm && (
          <div className="no-results">
            <p>No results found for "{searchTerm}"</p>
            <p className="search-tips">
              Try:
              <ul>
                <li>Using different search terms</li>
                <li>Checking spelling</li>
                <li>Using partial matches</li>
                <li>Switching search type</li>
              </ul>
            </p>
          </div>
        )}

        {!searchTerm && (
          <div className="search-placeholder">
            <h3>🔍 Search Chart Data</h3>
            <p>Enter a search term to find songs, artists, or albums across multiple charts.</p>
            <div className="search-examples">
              <h4>Search Examples:</h4>
              <ul>
                <li><strong>Artist:</strong> "Taylor Swift", "Drake", "The Weeknd"</li>
                <li><strong>Title:</strong> "Blinding Lights", "Shape of You", "Uptown Funk"</li>
                <li><strong>Album:</strong> "folklore", "Scorpion", "After Hours"</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper functions
const getChangeSymbol = (current, last) => {
  if (!last) return 'NEW';
  if (current < last) return '↑';
  if (current > last) return '↓';
  return '→';
};

const getChangeClass = (current, last) => {
  if (!last) return 'new';
  if (current < last) return 'up';
  if (current > last) return 'down';
  return 'same';
};

const formatPlaycount = (playcount) => {
  if (!playcount) return 'N/A';
  if (playcount >= 1000000) {
    return `${(playcount / 1000000).toFixed(1)}M`;
  }
  if (playcount >= 1000) {
    return `${(playcount / 1000).toFixed(1)}K`;
  }
  return playcount.toString();
};

export default ChartSearch; 